/**
 * RTCP connection module.
 * @version 1
 */
import {
  ALERT_LOW_BANDWIDTH,
  AUDIO_KBPS_UPDATED,
  AUDIO_PACKETS_LOST,
  CHANGE_BITRATE,
  CONNECTION_CLOSED,
  CONNECTION_READY,
  CURSOR_CHANGE_HIDE,
  CURSOR_CHANGE_MODE,
  CURSOR_CHANGE_POSITION,
  CURSOR_CHANGE_SHAPE,
  FILE_PROGRESS,
  FILE_TRANSFERRED,
  INBOUND_RTP_REPORT,
  INPUT_CHANNEL_READY,
  KBPS_UPDATED,
  LATENCY_CHECK_REQUESTED,
  MEDIA_STREAM_CANDIDATE_FLUSH,
  NETWORK_LATENCY_UPDATED,
  NOT_SUPPORTED,
  RETRY_CONNECTION,
  STREAM_ERROR,
  UNSTABLE_CONNECTION,
  VD_AGENT_CONNECTED,
  VD_AGENT_DISCONNECTED,
  mEvent,
} from './events.ts'
import socket from './socket'

const rtcp = (() => {
  let connection
  let inputChannel
  let mediaStreamVideo
  let mediaStreamAudio
  // let webcamStream

  let candidates = Array()
  let isAnswered = false
  let isFlushing = false

  let connected = false
  let inputReady = false

  let lastPacketsLost = 0
  let lastFramesDropped = 0
  // let alertTimeout = null

  let isTest = false

  let previousBytes = 0
  let previousTS = 0
  let currentBytes = 0
  let currentTS = 0

  let previousAudioBytes = 0
  let previousAudioTS = 0
  let currentAudioBytes = 0
  let currentAudioTS = 0

  let servers = []
  let initialOffer
  let retry = 0

  let platform
  let isMobile

  let allStats = {}

  const CONNECTION_TIMEOUT = 5000

  let statsInterval
  let connectionTimeout

  const start = (iceservers, p = 'UNKNOWN', m = false) => {
    servers = iceservers
    platform = p
    isMobile = m

    // Reset answered flag when starting a new connection
    isAnswered = false
    isFlushing = false
    candidates = []

    // Relay-only: force ICE through TURN so every session systematically uses
    // the single TURN port. coturn then dispatches to the internal streamer
    // address/port. No direct (host) nor STUN (server-reflexive) path.
    connection = new RTCPeerConnection({
      iceServers: servers,
      iceTransportPolicy: 'relay',
    })

    if (connectionTimeout) {
      clearTimeout(connectionTimeout)
      connectionTimeout = null
    }

    connectionTimeout = setTimeout(() => {
      console.debug(
        `[RTCP] ICE gathering was aborted due to timeout ${CONNECTION_TIMEOUT}ms.`
      )
      if (retry > 1 || isTest) {
        mEvent.dispatch(STREAM_ERROR, {
          id: 101,
          message: 'Veuillez redémarrer votre Cloud PC',
          type: 'close',
        })
      } else {
        console.debug('Reconnect...')
        retry += 1
        mEvent.dispatch(RETRY_CONNECTION, retry)
      }
    }, CONNECTION_TIMEOUT)

    mediaStreamVideo = new MediaStream()
    mediaStreamAudio = new MediaStream()

    connection.oniceconnectionstatechange = ice.onIceConnectionStateChange
    connection.onicegatheringstatechange = ice.onIceStateChange

    inputChannel = connection.createDataChannel('a', {
      ordered: true,
      negotiated: true,
      id: 0,
    })

    connection.ondatachannel = (e) => {
      inputChannel = e.channel

      inputChannel.onopen = () => {
        inputReady = true
        mEvent.dispatch(INPUT_CHANNEL_READY)
      }

      inputChannel.onclose = () => {
        console.debug('[RTCP] The input channel has closed.')
        mEvent.dispatch(CONNECTION_CLOSED)
      }

      inputChannel.onmessage = (response) => {
        let data

        try {
          data = JSON.parse(response.data)
        } catch (e) {
          data = { PType: 'unknown' }
        }

        const message = data.PType

        switch (message) {
          case 'change_mouse_shape':
            mEvent.dispatch(CURSOR_CHANGE_SHAPE, {
              cursorshape: data.Data,
            })
            break
          case 'change_mouse_position':
            mEvent.dispatch(CURSOR_CHANGE_POSITION, {
              cursorposition: data.Data,
            })
            break
          case 'change_mouse_mode':
            mEvent.dispatch(CURSOR_CHANGE_MODE, {
              cursormode: data.Data,
            })
            break
          case 'change_mouse_hide':
            mEvent.dispatch(CURSOR_CHANGE_HIDE, {
              cursorshape: '',
            })
            break
          case 'transfert_finished':
            const transfer = data.Data
            mEvent.dispatch(FILE_TRANSFERRED, {
              name: transfer.name,
              result: transfer.result,
              error: transfer.error,
            })
            break
          case 'transfer_progress':
            const progress = data.Data
            mEvent.dispatch(FILE_PROGRESS, {
              name: progress.name,
              progress: progress.progress,
            })
            break
          case 'checkLatency':
            const curPacketId = data.packet_id
            const addresses = data.data.split(',')
            mEvent.dispatch(LATENCY_CHECK_REQUESTED, {
              packetId: curPacketId,
              addresses: addresses,
            })
            break
          case 'change_bitrate':
            mEvent.dispatch(CHANGE_BITRATE, {
              bitrate: data.Data.Bitrate,
              maxBitrate: data.Data.MaxBitrate,
            })
            sleep(5)
            break
          case 'unstable_connection':
            mEvent.dispatch(UNSTABLE_CONNECTION)
            sleep(5)
            break
          case 'alert_low_bandwidth':
            mEvent.dispatch(ALERT_LOW_BANDWIDTH)
            sleep(5)
            break
          case 'change_agent_connected':
            mEvent.dispatch(VD_AGENT_CONNECTED)
            break
          case 'change_agent_disconnected':
            mEvent.dispatch(VD_AGENT_DISCONNECTED)
            break
          default:
            break
        }
      }
    }

    connection.onicecandidate = ice.onIcecandidate

    connection.ontrack = (event) => {
      if (event.track.kind === 'video' && event.track.readyState === 'live') {
        statsInterval = setInterval(getStats, 1000)
        mediaStreamVideo.addTrack(event.track)
      }

      if (event.track.kind === 'audio' && event.track.readyState === 'live') {
        mediaStreamAudio.addTrack(event.track)
        mediaStreamVideo.addTrack(event.track)
      }
    }
  }

  const stop = () => {
    if (mediaStreamVideo) {
      mediaStreamVideo.getTracks().forEach((track) => track.stop())
    }

    if (mediaStreamAudio) {
      mediaStreamAudio.getTracks().forEach((track) => track.stop())
    }

    if (connection) {
      connection.close()
      clearInterval(statsInterval)

      if (inputChannel) {
        inputChannel.close()
      }

      connection = null
      inputChannel = null
      mediaStreamVideo = null
      mediaStreamAudio = null

      // inputChannel.close()

      // mediaStreamVideo.getTracks().forEach((track) => track.stop())
      // mediaStreamAudio.getTracks().forEach((track) => track.stop())

      // Force garbage collection https://stackoverflow.com/a/66546935/1259869 //
      let img = document.createElement('img')
      img.src = window.URL.createObjectURL(new Blob([new ArrayBuffer(5e7)]))
      img.onerror = function () {
        window.URL.revokeObjectURL(this.src)
        img = null
      }

      candidates = []
      isAnswered = false
      isFlushing = false
      connected = false
      inputReady = false
      servers = []
      initialOffer = null
      allStats = {}
      statsInterval = null
    }
  }

  const setRemoteDescription = async (data, mediaVideo, mediaAudio) => {
    // Prevent setting remote description if already answered
    if (isAnswered) {
      console.warn(
        '[RTCP] Ignoring duplicate setRemoteDescription call - already answered'
      )

      return
    }

    // Check connection state before setting remote description
    if (!connection || connection.signalingState === 'closed') {
      console.error(
        '[RTCP] Cannot set remote description - connection is closed'
      )

      return
    }

    // Only accept "offer" type when in "stable" state
    if (connection.signalingState === 'stable' && isAnswered) {
      console.warn(
        '[RTCP] Cannot set remote description in stable state after answer'
      )

      return
    }

    initialOffer = data
    const offer = new RTCSessionDescription(JSON.parse(atob(data)))

    try {
      await connection.setRemoteDescription(offer)
    } catch (e) {
      console.error('[RTCP] Failed to set remote description:', e)
      mEvent.dispatch(NOT_SUPPORTED)
    }

    let answer = null

    try {
      answer = await connection.createAnswer()
    } catch (e) {
      mEvent.dispatch(STREAM_ERROR, { id: 102, error: e })
    }

    // Chrome bug https://bugs.chromium.org/p/chromium/issues/detail?id=818180 workaround
    // force stereo params for Opus tracks (a=fmtp:111 ...)
    if (answer == null) {
      mEvent.dispatch(NOT_SUPPORTED)
      return
    }

    answer.sdp = answer.sdp.replace(
      /(a=fmtp:111 .*)/g,
      '$1;stereo=1;sprop-stereo=1'
    )

    await connection.setLocalDescription(answer)

    isAnswered = true
    mEvent.dispatch(MEDIA_STREAM_CANDIDATE_FLUSH)

    socket.send({
      id: 'answer',
      data: btoa(JSON.stringify(answer)),
    })

    if (mediaVideo) {
      mediaVideo.srcObject = mediaStreamVideo
    }

    if (mediaAudio) {
      mediaAudio.srcObject = mediaStreamAudio
    }
  }

  const getStats = async () => {
    if (!rtcp.isConnected()) {
      console.error('[RTCP] Attempted to get stats on a closed connection.')

      return
    }

    rtcp
      .getConnection()
      .getStats()
      .catch(() => {
        clearInterval(statsInterval)
        statsInterval = null
      })
      .then((stats) => {
        if (!stats) {
          return
        }

        stats.forEach((report) => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            mEvent.dispatch(INBOUND_RTP_REPORT, JSON.stringify(report))

            if (
              report.packetsLost !== lastPacketsLost
              || report.framesDropped !== lastFramesDropped
            ) {
              mEvent.dispatch(UNSTABLE_CONNECTION)
            }

            allStats = {
              packetsLost: report.packetsLost,
              packetsReceived: report.packetsReceived,
            }

            lastPacketsLost = report.packetsLost
            lastFramesDropped = report.framesDropped

            currentBytes = report.bytesReceived
            currentTS = report.timestamp

            if (previousBytes === 0) {
              previousBytes = currentBytes
              previousTS = currentTS
              return
            }

            let deltaBytes = currentBytes - previousBytes
            let deltaTS = currentTS - previousTS

            mEvent.dispatch(KBPS_UPDATED, deltaBytes / deltaTS)
            previousBytes = currentBytes
            previousTS = currentTS
          }

          if (report.type === 'inbound-rtp' && report.kind === 'audio') {
            mEvent.dispatch(AUDIO_PACKETS_LOST, report.packetsLost)

            currentAudioBytes = report.bytesReceived
            currentAudioTS = report.timestamp

            if (previousAudioBytes === 0) {
              previousAudioBytes = currentAudioBytes
              previousAudioTS = currentAudioTS
              return
            }

            let deltaBytes = currentAudioBytes - previousAudioBytes
            let deltaTS = currentAudioTS - previousAudioTS

            mEvent.dispatch(AUDIO_KBPS_UPDATED, deltaBytes / deltaTS)

            previousAudioBytes = currentAudioBytes
            previousAudioTS = currentAudioTS
          }

          if (
            report.type === 'candidate-pair'
            && report.state === 'succeeded'
          ) {
            const latency = report.currentRoundTripTime
              ? report.currentRoundTripTime * 1000
              : report.totalRoundTripTime && report.responsesReceived
                ? (report.totalRoundTripTime / report.responsesReceived) * 1000
                : null

            if (latency !== null) {
              mEvent.dispatch(NETWORK_LATENCY_UPDATED, latency)
            }
          }
        })
      })
  }

  const ice = (() => {
    let isGatheringDone = false

    return {
      onIcecandidate: (event) => {
        // this trigger when setRemoteDesc success
        // send any candidate to worker
        if (event.candidate != null) {
          // Relay-only: every gathered candidate is a TURN relay candidate and
          // must be forwarded to the worker.
          let candidate = JSON.stringify(event.candidate)
          socket.send({
            id: 'candidate',
            data: btoa(candidate),
          })
        }
      },
      onIceStateChange: (event) => {
        switch (event.target.iceGatheringState) {
          case 'gathering':
            break
          case 'complete':
        }
      },
      onIceConnectionStateChange: () => {
        if (!connection) {
          return
        }

        switch (connection.iceConnectionState) {
          case 'connected': {
            if (connectionTimeout) {
              clearTimeout(connectionTimeout)
              connectionTimeout = null
            }

            connected = true

            if (!statsInterval) {
              statsInterval = setInterval(getStats, 1000)
            }

            mEvent.dispatch(
              CONNECTION_READY,
              typeof connection.sctp.transport.iceTransport
                .getSelectedCandidatePair === 'function'
                ? (connection.sctp.transport.iceTransport.getSelectedCandidatePair()
                    ?? {})
                : {}
            )

            break
          }
          case 'disconnected': {
            connected = false
            clearInterval(statsInterval)
            statsInterval = null
            mEvent.dispatch(CONNECTION_CLOSED)

            break
          }
          case 'failed': {
            connected = false
            connection
              .createOffer({
                iceRestart: true,
              })
              .then((description) =>
                connection.setLocalDescription(description).catch(() => {
                  mEvent.dispatch(STREAM_ERROR, { id: 102 })
                })
              )
              .catch(() => {
                mEvent.dispatch(STREAM_ERROR, { id: 102 })
              })

            break
          }
          case 'closed': {
            connected = false
            clearInterval(statsInterval)
            statsInterval = null

            break
          }
        }
      },
    }
  })()

  return {
    start: start,
    setRemoteDescription: setRemoteDescription,
    addWebcam: async () => {
      let stream = null

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            frameRate: { min: 30 },
            width: { min: 1280 },
            height: { min: 720 },
          },
          audio: true,
        })

        stream.getTracks().forEach(function (track) {
          connection.addTrack(track)
        })
      } catch (error) {
        console.error(
          'addVoiceStream: Error getting audio stream from getUserMedia.'
        )
        console.error(error)
        mEvent.dispatch(STREAM_ERROR, { id: 202 })
      }
    },

    addCandidate: (data) => {
      if (data === '') {
        mEvent.dispatch(MEDIA_STREAM_CANDIDATE_FLUSH)
      } else {
        let d = atob(data)
        candidates.push(data)
      }
    },

    flushCandidate: () => {
      if (isFlushing || !isAnswered) {
        return
      }

      isFlushing = true
      candidates.forEach((data) => {
        let d = atob(data)
        let candidate = new RTCIceCandidate(JSON.parse(d))
        connection.addIceCandidate(candidate)
      })
      isFlushing = false
    },

    inputSend: (data) => {
      if (!connected || !inputReady || inputChannel.readyState !== 'open') {
        console.debug(
          'RTCP input has been dropped because RTCP connection is not opened.',
          data
        )

        return
      }

      console.debug('RTCP input sending', data)

      inputChannel.send(JSON.stringify(data))
    },
    isConnected: () => connected,
    isInputReady: () => inputReady,
    getConnection: () => connection,
    isTest: (value) => (isTest = value),
    allStats: () => allStats,
    stop: stop,
  }
})(mEvent, socket)

export default rtcp
